import { useEffect, useState } from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'

import { F0Box } from '@factorialco/f0-react'
import { F0ActionBar, SectionHeader } from '@factorialco/f0-react/dist/experimental'
import {
  DeleteBudgetMovementDocument,
  TrainingBudgetsDocument,
  TrainingClassDetailIndexPageDocument,
  TrainingsTabsDocument,
  TrainingsTrainingClassPaymentStatusEnum,
  UpdateCostsClassDetailDocument,
  UpdateCostsClassDetailMutationVariables,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { checkFeatures } from 'modules/core/hooks/useFeatures'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavConfirmModal } from 'modules/core/hooks/useNavConfirmModal'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { SalaryCostCalculator } from 'modules/trainings/components/SalaryCostCalculator'

import { BudgetLinkBanner } from './components/BudgetLinkBanner'
import { CostBreakdownCard } from './components/CostBreakdownCard'
import { CostsSummaryRow } from './components/CostsSummaryRow'
import { OtherCostsSection } from './components/OtherCostsSection'

interface ClassCostsValues {
  cost: string
  subsidizedCost?: string
  indirectCost?: string
  salaryCost?: string
  paymentStatus?: TrainingsTrainingClassPaymentStatusEnum | null
  budgetId?: string | null
}

const MAX_COST_VALUE = 999999

const CostsTabNew = () => {
  const { classId } = useParams()
  const navigate = useNavigate()
  const { company } = useCurrentSession()
  const { canEdit } = useOutletContext<{ canEdit: boolean }>()

  const { data } = useQuery(TrainingClassDetailIndexPageDocument, {
    variables: { companyId: company.id, trainingClassId: classId ?? '', withFF: true },
  })
  const currentClass = data?.trainings.trainingClass ?? undefined

  const { has_dev_training_budgets } = checkFeatures(
    ['dev_training_budgets'],
    data?.features?.featuresConnection.nodes
  )

  const { data: tabsData } = useQuery(TrainingsTabsDocument, {
    variables: { companyId: company.id },
    skip: !has_dev_training_budgets,
  })
  const canManageTrainingBudgets =
    tabsData?.finance?._policies?.budgetsManageTrainingBudgets?.can
  const canLinkBudget = canManageTrainingBudgets && canEdit

  const { data: budgetsData } = useQuery(TrainingBudgetsDocument, {
    variables: {
      budgetableBy: ['training_class'],
    },
    skip: !has_dev_training_budgets,
  })

  const currency = currentClass?.currency || company.currency || ''

  // Show all budgets (currency conversion is handled server-side)
  const allBudgets = budgetsData?.finance.budgetsConnection.edges || []
  const hasBudgets = allBudgets.length > 0

  const linkedBudget = currentClass?.budgetMovement?.budget
  const isLinkedBudgetArchived = linkedBudget?.archived ?? false

  const budgetOptions = [
    ...allBudgets.map((edge) => ({
      value: String(edge.node.id),
      label: `${edge.node.name} (${edge.node.currency})`,
    })),
    ...(isLinkedBudgetArchived && linkedBudget
      ? [{ value: String(linkedBudget.id), label: linkedBudget.name }]
      : []),
  ]

  const [updateCosts] = useMutation(UpdateCostsClassDetailDocument, {
    extractorFn(d) {
      return d.trainingsMutations.updateTrainingsTrainingClass
    },
  })

  const [deleteBudgetMovement] = useMutation(DeleteBudgetMovementDocument, {
    extractorFn: (data) => data.financeMutations.deleteFinanceBudgetMovement,
  })

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const { NavConfirmModal, setNavigationBlocked } = useNavConfirmModal()

  useEffect(() => {
    setNavigationBlocked(hasUnsavedChanges)
  }, [hasUnsavedChanges, setNavigationBlocked])

  const { values, setValues, handleSubmit, errors, validateForm, resetForm } =
    useForm<ClassCostsValues>({
      enableReinitialize: true,
      defaultValues: {
        cost: currentClass?.cost || '',
        subsidizedCost: currentClass?.subsidizedCost || '',
        indirectCost: currentClass?.indirectCost || '',
        salaryCost: currentClass?.salaryCost || '',
        paymentStatus: currentClass?.paymentStatus,
        budgetId: currentClass?.budgetMovement?.budget?.id ?? null,
      },
      validate: (formData) => {
        const validationErrors: FormErrors<ClassCostsValues> = {}
        if (Number(formData.cost) < 0) {
          validationErrors.cost = i18n.t('trainings.classes.errors.greater_than_zero')
        }
        if (Number(formData.cost) > MAX_COST_VALUE) {
          validationErrors.cost = i18n.t('trainings.classes.errors.less_than')
        }
        if (Number(formData.subsidizedCost) < 0) {
          validationErrors.subsidizedCost = i18n.t('trainings.classes.errors.greater_than_zero')
        }
        if (Number(formData.subsidizedCost) > MAX_COST_VALUE) {
          validationErrors.subsidizedCost = i18n.t('trainings.classes.errors.less_than')
        }
        if (Number(formData.indirectCost) < 0) {
          validationErrors.indirectCost = i18n.t('trainings.classes.errors.greater_than_zero')
        }
        if (Number(formData.indirectCost) > MAX_COST_VALUE) {
          validationErrors.indirectCost = i18n.t('trainings.classes.errors.less_than')
        }
        if (Number(formData.salaryCost) < 0) {
          validationErrors.salaryCost = i18n.t('trainings.classes.errors.greater_than_zero')
        }
        if (Number(formData.salaryCost) > MAX_COST_VALUE) {
          validationErrors.salaryCost = i18n.t('trainings.classes.errors.less_than')
        }
        return validationErrors
      },
      onSubmit: async (classData) => {
        setIsSaving(true)
        try {
          const payload: UpdateCostsClassDetailMutationVariables = {
            id: currentClass?.id ?? '',
            ...classData,
          }

          // If budget was linked and is now being cleared, delete the movement first
          const budgetMovementId = currentClass?.budgetMovement?.id
          const isClearing = budgetMovementId && !payload.budgetId

          if (isClearing) {
            await deleteBudgetMovement({
              variables: { ids: [budgetMovementId] },
            })
          }

          const result = await updateCosts({
            variables: {
              id: payload.id,
              cost: payload.cost && payload.cost.length > 0 ? payload.cost : '0',
              salaryCost:
                payload.salaryCost && payload.salaryCost?.length > 0 ? payload.salaryCost : '0',
              indirectCost:
                payload.indirectCost && payload.indirectCost.length > 0
                  ? payload.indirectCost
                  : '0',
              subsidizedCost:
                payload.subsidizedCost && payload.subsidizedCost?.length > 0
                  ? payload.subsidizedCost
                  : '0',
              paymentStatus: payload.paymentStatus,
              ...(payload.budgetId ? { budgetId: payload.budgetId } : {}),
            },
            refetchQueries: [TrainingClassDetailIndexPageDocument, TrainingBudgetsDocument],
          })
          if (result) {
            const handled = handleResult(result, () => {})
            if (handled.success) {
              setHasUnsavedChanges(false)
            }
            return handled
          }
          return Promise.reject()
        } finally {
          setIsSaving(false)
        }
      },
    })

  const handleFieldChange = async (
    field: keyof ClassCostsValues,
    newValue: string | TrainingsTrainingClassPaymentStatusEnum | number | null
  ) => {
    const updatedPaymentStatus: ClassCostsValues['paymentStatus'] =
      field === 'budgetId' && newValue != null && !values.paymentStatus
        ? 'pending'
        : values.paymentStatus

    const newValues = {
      ...values,
      paymentStatus: updatedPaymentStatus,
      [field]: newValue,
    }

    setValues(newValues)
    setHasUnsavedChanges(true)
    await validateForm(newValues)
  }

  const handleDiscard = () => {
    resetForm()
    setHasUnsavedChanges(false)
  }

  // Calculate total cost in cents for display (sum of direct, indirect, and salary costs)
  // Use Math.round to ensure proper integer cents values
  const totalCostCents = Math.round(
    (Number(values.cost) || 0) * 100 +
      (Number(values.indirectCost) || 0) * 100 +
      (Number(values.salaryCost) || 0) * 100
  )

  // Get selected budget info for the banner
  const selectedBudget = values.budgetId
    ? allBudgets.find((edge) => edge.node.id === values.budgetId)?.node
    : null

  const selectedBudgetInfo =
    isLinkedBudgetArchived && linkedBudget
      ? {
          id: linkedBudget.id,
          name: linkedBudget.name,
          amountCents: 0,
          spentAmountCents: 0,
          availableAmountCents: 0,
          currency: linkedBudget.currency,
        }
      : selectedBudget
        ? {
            id: selectedBudget.id,
            name: selectedBudget.name,
            amountCents: Number(selectedBudget.amountCents),
            spentAmountCents: Number(selectedBudget.spentAmountCents),
            availableAmountCents: Number(selectedBudget.availableAmountCents),
            currency: selectedBudget.currency,
          }
        : null

  // If the training is already linked to the selected budget, we need to account
  // for its current amount when calculating available budget
  // Use Number() to handle potential BigInt values from GraphQL
  const currentlyLinkedBudgetId = currentClass?.budgetMovement?.budget?.id
  const currentLinkedAmountCents =
    values.budgetId && currentlyLinkedBudgetId === values.budgetId
      ? Number(currentClass?.budgetMovement?.amountCents ?? 0)
      : 0

  return (
    <>
      <F0Box display="flex" flexDirection="column" width="full" gap="xl">
        <SectionHeader
          title={i18n.t('trainings.classes.costs.header.title')}
          description={i18n.t('trainings.classes.costs.header.description')}
        />

        {/* Archived Budget Banner */}
        {has_dev_training_budgets && isLinkedBudgetArchived && (
          <BudgetLinkBanner
            budgetId={values.budgetId ?? null}
            selectedBudget={selectedBudgetInfo}
            totalGroupCostCents={totalCostCents}
            hasBudgets={hasBudgets}
            trainingCurrency={currency}
            currentLinkedAmountCents={currentLinkedAmountCents}
            canManageBudgets={canLinkBudget}
            isLinkedBudgetArchived={isLinkedBudgetArchived}
          />
        )}

        <F0Box display="flex" paddingTop="xl" paddingBottom="xl" flexDirection="column" gap="lg">
          {/* Summary Row: Total cost + Budget selector + Payment status */}
          <CostsSummaryRow
            totalCostCents={totalCostCents}
            currency={currency}
            budgetOptions={budgetOptions}
            selectedBudgetId={values.budgetId ?? null}
            paymentStatus={values.paymentStatus ?? null}
            onBudgetChange={(budgetId) => handleFieldChange('budgetId', budgetId)}
            onPaymentStatusChange={(status) => handleFieldChange('paymentStatus', status)}
            hasBudgetsFeature={has_dev_training_budgets}
            budgetDisabled={!canLinkBudget || isLinkedBudgetArchived}
          />

          {/* Budget Link Banner (non-archived states) */}
          {has_dev_training_budgets && !isLinkedBudgetArchived && (
            <BudgetLinkBanner
              budgetId={values.budgetId ?? null}
              selectedBudget={selectedBudgetInfo}
              totalGroupCostCents={totalCostCents}
              hasBudgets={hasBudgets}
              trainingCurrency={currency}
              currentLinkedAmountCents={currentLinkedAmountCents}
              canManageBudgets={canLinkBudget}
            />
          )}

          {/* Cost Breakdown Cards */}
          <F0Box
            display="grid"
            columns="3"
            gap="xl"
            width="full"
            paddingTop="xl"
            paddingBottom="xl"
          >
            <CostBreakdownCard
              emoji="💰"
              title={i18n.t('trainings.classes.costs.direct.title')}
              description={i18n.t('trainings.classes.costs.direct.description')}
              value={values.cost ? Number(values.cost) : null}
              currency={currency}
              onChange={(value) => handleFieldChange('cost', value !== null ? String(value) : '')}
              error={errors.cost}
            />

            <CostBreakdownCard
              emoji="🏢"
              title={i18n.t('trainings.classes.costs.indirect.title')}
              description={i18n.t('trainings.classes.costs.indirect.description')}
              value={values.indirectCost ? Number(values.indirectCost) : null}
              currency={currency}
              onChange={(value) =>
                handleFieldChange('indirectCost', value !== null ? String(value) : '')
              }
              error={errors.indirectCost}
            />

            <CostBreakdownCard
              emoji="📝"
              title={i18n.t('trainings.classes.costs.salary.title')}
              description={i18n.t('trainings.classes.costs.salary.description')}
              value={values.salaryCost ? Number(values.salaryCost) : null}
              currency={currency}
              onChange={(value) =>
                handleFieldChange('salaryCost', value !== null ? String(value) : '')
              }
              error={errors.salaryCost}
              action={
                <SalaryCostCalculator
                  classId={currentClass?.id ?? ''}
                  onApply={(newValue) => {
                    handleFieldChange('salaryCost', newValue)
                    return true
                  }}
                  trackingSelectedFrom="groups_cost_tab"
                  currency={currency}
                />
              }
            />
          </F0Box>

          {/* Other Costs Section */}
          <OtherCostsSection
            subsidizedCost={values.subsidizedCost ? Number(values.subsidizedCost) : null}
            currency={currency}
            onChange={(value) =>
              handleFieldChange('subsidizedCost', value !== null ? String(value) : '')
            }
            error={errors.subsidizedCost}
          />
        </F0Box>
      </F0Box>

      <NavConfirmModal />

      {hasUnsavedChanges && (
        <F0ActionBar
          isOpen
          primaryActions={[
            {
              label: i18n.t('trainings.content.builder.save'),
              onClick: handleSubmit,
              disabled: isSaving,
            },
          ]}
          secondaryActions={[
            { label: i18n.t('trainings.content.builder.discard'), onClick: handleDiscard },
          ]}
          label={i18n.t('trainings.content.builder.unsaved_changes')}
        />
      )}
    </>
  )
}

export default CostsTabNew
